import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { BillOut } from "../../../../api/bills";
import { useDiscountClients } from "../../../../hooks/useDiscounts";
import { useUsers } from "../../../../hooks/useUsers";
import { useBillDiscountLocked, useIsEdgeLocal } from "../../../../hooks/useEdgeMode";
import { useAppSelector } from "../../../../store/hooks";
import { tableCardFactsOf } from "./cardInfo/tableCardFacts";
import { billTableNames } from "./schema/orderingFloorData";

/** Производните полета на сметката — за картата и за търсачката на списъка. */
export interface TableCardInfo {
  bill: BillOut;
  waiterLabel: string | null;
  clientLabel: string | null;
  reservationGuestName: string | null;
  total: number;
}

/**
 * Какво носи картата на една маса (`TableCard`) в списъка (`TableList`) —
 * името, сумата, сервитьорът (само на чужди маси), закачен ли е клиент (цветът
 * на името), „Закрива се" и фактите за сметката (`tableCardFactsOf`:
 * резервация, гости, „Карта — без бон", депозит, клиент, отстъпки); търсачката
 * намира и гостите на настанената резервация. Сервитьора ползва и заглавието
 * на панела в режим „Схема" (`describe`).
 */
export const useTableCardInfo = () => {
  const { t } = useTranslation();
  const { data: users = [] } = useUsers();
  const { data: discountData } = useDiscountClients();
  const discountClients = useMemo(
    () => discountData?.items ?? [],
    [discountData],
  );

  // С право view_all_users_bills списъкът съдържа и чужди
  // маси — на тях показваме името на сервитьора, за да е ясно чия е масата.
  const permissions = useAppSelector((state) => state.auth.permissions);
  const currentUserId = useAppSelector((state) => state.auth.userId);
  const seesAllUsersBills = permissions.some(
    (p) => p.name === "view_all_users_bills",
  );
  // Локален режим (docs/edge.md): касата държи копие на последния облачен
  // списък и пази `reservation` в него, но „Гостите си тръгнаха" / „Отмени
  // настаняването" на касата не го махат — кутийката и търсачката биха
  // излъгали. Затова там името на гостите не се показва и не се търси.
  const edgeLocal = useIsEdgeLocal();
  const billDiscountLocked = useBillDiscountLocked();

  const describe = useCallback(
    (bill: BillOut): TableCardInfo => {
      const waiter = users.find((u) => u.id === bill.waiter_id);
      const client = bill.customer_id
        ? (discountClients.find((c) => c.id === bill.customer_id) ?? null)
        : null;
      return {
        bill,
        waiterLabel: waiter ? waiter.names || waiter.username : null,
        clientLabel: client ? client.name : null,
        reservationGuestName: edgeLocal
          ? null
          : (bill.reservation?.guest_name ?? null),
        total: parseFloat(bill.total_amount),
      };
    },
    [users, discountClients, edgeLocal],
  );

  /** Props-ите на `TableCard` без избора, търсачката и кликовете. */
  const cardPropsOf = (
    { bill, waiterLabel, clientLabel, reservationGuestName, total }: TableCardInfo,
    closingBillIds?: ReadonlySet<number>,
  ) => {
    // Името на сервитьора се показва само на чужди маси — своите нямат нужда
    // от етикет.
    const showWaiter =
      seesAllUsersBills &&
      bill.waiter_id != null &&
      bill.waiter_id !== currentUserId;
    const isClosedEmpty = bill.status === "closed" && parseFloat(bill.total) === 0;
    const isClosing = closingBillIds?.has(bill.id) ?? false;
    return {
      name: bill.name,
      // „Една сметка" за резервация на няколко маси — „14 | 13 | 12"; в
      // локален режим резервацията е остаряло копие — само името.
      tableNames: edgeLocal ? [bill.name] : billTableNames(bill),
      total,
      waiterLabel: showWaiter ? waiterLabel : null,
      // Закачен клиент — по документа на сметката, а справочникът е резерва
      // (асинхронен; изтрит клиент там е null).
      hasClient: bill.discount_applied != null || clientLabel !== null,
      isClosedEmpty,
      isClosing,
      facts: tableCardFactsOf(bill, {
        t,
        clientLabel,
        reservationGuestName,
        edgeLocal,
        billDiscountLocked,
        isClosedEmpty,
        isClosing,
      }),
    };
  };

  return { describe, cardPropsOf };
};
